/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable, NgZone } from '@angular/core';

import { Coordinate } from 'ol/coordinate';
import { toLonLat } from 'ol/proj';

import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { ActiveMapState } from '../state/active-map.state';
import { LoggerService } from '../../shared/services/logger.service';
import { LaunchStreetView, SetGoogleMapType, SetGoogleMaxImageryZoom } from '../state/active-map.actions';
import { map, mergeMap, take } from 'rxjs/operators';
import { SetThemeVariant } from '../../../core/ui/theme-switcher/state/theme.actions';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { throttle } from 'lodash-es';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { MaxZoomPayload } from '../state/active-map.model';
import { MeasureService } from '../../shared/services/measure.service';
import { ThemeState } from '../../../core/ui/theme-switcher/state/theme.state';
import { MapConfigService } from './mapConfig.service';
import { GoogleBaseLayerConfig } from '../../../types';
import { RestService } from '../../shared/services/rest.service';
import { AddressComponentDictionary, StreetViewMetadata } from '../../reports/state/report.model';
import MaxZoomResult = google.maps.MaxZoomResult;

@Injectable()
export class GoogleMapService {
    gmap: google.maps.Map;
    googleMapElement: HTMLDivElement;

    setCenter: (center: Coordinate) => void;
    setZoom: (zoom: number) => void;

    private maxZoomService: any;
    private _apiKey: string;
    private streetViewVisibleSubject = new BehaviorSubject<boolean>(false);
    private streetViewPositionChangedSubject = new Subject<void>();
    private _placesService: google.maps.places.PlacesService;
    private readonly setMaxZoom: (center: { lat: any; lng: any }) => void;
    private trafficLayer: google.maps.TrafficLayer;

    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private actions$: Actions,
        private store: Store,
        private ngZone: NgZone,
        private mapConfig: MapConfigService
    ) {
        this.actions$
            .pipe(ofActionSuccessful(SetGoogleMapType))
            .subscribe((action: SetGoogleMapType) => this.setGoogleMapType(action.payload));

        this.actions$.pipe(ofActionSuccessful(SetThemeVariant)).subscribe((action) => {
            this.setThemeVariant(action.payload.variant);
        });

        this.actions$
            .pipe(ofActionSuccessful(LaunchStreetView))
            .subscribe((action) => this.launchStreetView(action.payload.pano, action.payload.heading));

        this.setCenter = this._setCenter;
        this.setZoom = this._setZoom;
        this.setMaxZoom = this._getMaxZoomFn();
    }

    initGoogleMap(streetViewElement: HTMLDivElement, layerConfig: GoogleBaseLayerConfig) {
        if (window.google?.maps) {
            const themeVariant = this.store.selectSnapshot(ThemeState.variant);
            this.store
                .select(ActiveMapState.state)
                .pipe(take(1))
                .subscribe((state) => {
                    const centerLatLon = toLonLat(state.center);
                    const googleCenter = new google.maps.LatLng(centerLatLon[1], centerLatLon[0]);
                    if (!this.maxZoomService) {
                        this.maxZoomService = new google.maps.MaxZoomService();
                        this.setMaxZoom(googleCenter);
                    }

                    if (!this.gmap) {
                        this.ngZone.runOutsideAngular(() => {
                            this.gmap = new google.maps.Map(this.googleMapElement, {
                                center: googleCenter,
                                mapTypeId: layerConfig.initialLayer,
                                disableDefaultUI: true,
                                streetViewControl: layerConfig.streetView || false,
                                gestureHandling: 'none',
                                styles: layerConfig.styles[themeVariant],
                                tilt: 0,
                                zoom: state.zoom,
                                animatedZoom: false,
                            } as any);

                            google.maps.event.addListener(this.gmap, 'projection_changed', () => {
                                const currentCenter = this.gmap.getCenter();
                                const payload: MaxZoomPayload = {
                                    maxZoomCenter: [currentCenter.lat(), currentCenter.lng()],
                                    googleMaxZoom: this.gmap.mapTypes[this.gmap.getMapTypeId()].maxZoom,
                                };
                                this.store.dispatch(new SetGoogleMaxImageryZoom(payload));
                            });
                        });
                    } else {
                        // if reusing the google map object, then reinitialize the StreetViewPano
                        this.ngZone.runOutsideAngular(() => {
                            this.gmap.setStreetView(null);
                        });
                    }
                    this.setCenter(state.center);
                    this.setZoom(state.zoom);

                    if (layerConfig.streetView) {
                        if (streetViewElement) {
                            const streetViewPano = new google.maps.StreetViewPanorama(streetViewElement);
                            streetViewPano.setVisible(false);
                            streetViewPano.addListener('visible_changed', () => {
                                this.ngZone.run(() => {
                                    this.streetViewVisibleSubject.next(streetViewPano.getVisible());
                                });
                            });
                            streetViewPano.addListener('position_changed', () => {
                                this.ngZone.runOutsideAngular(() => {
                                    this.streetViewPositionChangedSubject.next();
                                });
                            });
                            this.gmap.setStreetView(streetViewPano);
                        } else {
                            this.logger.error('Could not find the streetview pano element [#streetview-pano]');
                        }
                    }
                });
        }
    }

    controlTrafficLayer(active: boolean) {
        if (!this.trafficLayer) {
            this.trafficLayer = new google.maps.TrafficLayer();
        }
        if (active) {
            this.trafficLayer.setMap(this.gmap);
        } else {
            this.trafficLayer.setMap(null);
        }
    }

    refreshDisplay() {
        google.maps.event.trigger(this.gmap, 'resize');
    }

    hideStreetView() {
        if (this.apiAvailable) {
            this.gmap.getStreetView().setVisible(false);
            this.streetViewVisibleSubject.next(false);
        }
    }

    getStreetViewMetadata$(latLon: string, address?: string): Observable<any> {
        if (this.apiAvailable) {
            if (!latLon) {
                return of({});
            }

            const options = {
                size: '400x200',
                location: address || latLon,
                source: 'outdoor',
            };
            const imageBaseUrl = 'https://maps.googleapis.com/maps/api/streetview/metadata';

            return this.signApiUrl$(imageBaseUrl, {
                ...options,
                key: this.apiKey,
            }).pipe(
                mergeMap((signature) =>
                    this.rest.get(
                        imageBaseUrl,
                        {
                            ...options,
                            key: this.apiKey,
                            signature,
                        },
                        { responseType: 'json' }
                    )
                ),
                map((metadata: StreetViewMetadata) => {
                    if (metadata.status === google.maps.StreetViewStatus.OK) {
                        const latLngParts = latLon.split(',');
                        const latLngLiteral: google.maps.LatLngLiteral = {
                            lat: parseFloat(latLngParts[0]),
                            lng: parseFloat(latLngParts[1]),
                        };
                        const from = new google.maps.LatLng(metadata.location.lat, metadata.location.lng);
                        const to = new google.maps.LatLng(latLngLiteral);

                        // computeHeading returns -180 to 180, streetview accepts 0 to 360
                        const computedHeading = google.maps.geometry.spherical.computeHeading(from, to);
                        metadata.heading = (computedHeading + 360) % 360;
                    }

                    return metadata;
                })
            );
        }
        return of({});
    }

    getStaticStreetViewUrl$(metadata: StreetViewMetadata) {
        if (this.apiAvailable) {
            // filter metadata fields using whitelist - these will be used as URL params
            const allowed = ['heading', 'pano_id', 'size'];

            let options = Object.keys(metadata)
                .filter((key) => allowed.includes(key))
                .reduce((dict, key) => {
                    if (key === 'pano_id') {
                        dict['pano'] = metadata[key];
                    } else {
                        dict[key] = metadata[key];
                    }
                    return dict;
                }, {});

            options = {
                size: '400x240',
                source: 'outdoor',
                ...options,
            };
            const imageBaseUrl = 'https://maps.googleapis.com/maps/api/streetview';

            return this.signApiUrl$(imageBaseUrl, {
                ...options,
                key: this.apiKey,
            }).pipe(
                map((signature) =>
                    this.stringifyUrl(imageBaseUrl, {
                        ...options,
                        key: this.apiKey,
                        signature,
                    })
                )
            );
        }
        return of('');
    }

    getPlaceId$(address: string): Observable<string> {
        if (this.apiAvailable) {
            const infoSubject = new Subject<string>();
            if (address) {
                this.placesService.findPlaceFromQuery(
                    {
                        query: address,
                        fields: ['place_id'],
                    },
                    (result, status) => {
                        const placeResult =
                            status === google.maps.places.PlacesServiceStatus.OK ? result[0].place_id : null;
                        infoSubject.next(placeResult);
                    }
                );
            } else {
                setTimeout(() => infoSubject.next(null), 0);
            }
            return infoSubject.asObservable();
        }
        return of('');
    }

    getPlaceDetails$(placeId: string, fields: string[]): Observable<google.maps.places.PlaceResult> {
        if (!placeId) {
            return of(null);
        }

        const infoSubject = new Subject<google.maps.places.PlaceResult>();
        this.placesService.getDetails(
            {
                placeId,
                fields,
            },
            (result, status) => {
                const detailResult = status === google.maps.places.PlacesServiceStatus.OK ? result : null;
                infoSubject.next(detailResult);
            }
        );
        return infoSubject;
    }

    signApiUrl$(
        url: string,
        params: {
            [param: string]: string | ReadonlyArray<string>;
        }
    ): Observable<string> {
        const urlString = this.stringifyUrl(url, params, true);
        // const urlString = `${url}?${this.getUrlParamsString(params)}`;
        const signUrl = btoa(urlString).replace('+', '-').replace('/', '_');
        return this.rest.apiGet('map/sign', { url: signUrl }, { responseType: 'text' });
    }

    constructAddressFromComponents(components: google.maps.GeocoderAddressComponent[]) {
        const parts = this.transformAddressComponents(components);

        const line1 = this.constructAddressLine1(parts);
        const line2 = this.constructAddressLine2(parts);
        return {
            address1: line1,
            address2: line2,
        };
    }

    constructAddressLine1(parts: AddressComponentDictionary) {
        const lineComponents = [];
        if (parts.street_number) {
            lineComponents.push(parts.street_number);
        }

        if (parts.route) {
            lineComponents.push(parts.route);
        }

        return lineComponents.join(' ');
    }

    constructAddressLine2(parts: AddressComponentDictionary) {
        const lineComponents = [];
        if (parts.locality) {
            let optionalComma = '';
            if (parts.locality && parts.administrative_area_level_1) {
                optionalComma = ',';
            }
            lineComponents.push(parts.locality + optionalComma);
        }

        if (parts.administrative_area_level_1) {
            lineComponents.push(parts.administrative_area_level_1);
        }

        if (parts.postal_code) {
            lineComponents.push(parts.postal_code);
        }

        return lineComponents.join(' ');
    }

    launchStreetView(pano: string, heading?: number) {
        this.streetViewVisibleSubject
            .asObservable()
            .pipe(take(1))
            .subscribe(() => {
                this.gmap.getStreetView().setPano(pano);
                if (heading) {
                    this.gmap.getStreetView().setOptions({
                        pov: {
                            heading,
                            pitch: 0,
                        },
                    });
                }
                setTimeout(() => {
                    this.gmap.getStreetView().setVisible(true);
                }, 500);
            });
        this.streetViewVisibleSubject.next(true);
    }

    private transformAddressComponents(components: google.maps.GeocoderAddressComponent[]): AddressComponentDictionary {
        return components.reduce((dict, { short_name, types }) => {
            types.forEach((t) => (dict[t] = short_name));

            return dict;
        }, {});
    }

    private stringifyUrl(
        url: string,
        params: {
            [param: string]: string | ReadonlyArray<string>;
        },
        stripDomain = false
    ) {
        const urlObject = new URL(url);

        // use HttpParams here so the result is consistent with requests made through HttpClient
        const httpParams = this.rest.createParams(params);
        return stripDomain
            ? `${urlObject.pathname}?${httpParams.toString()}`
            : `${urlObject.href}?${httpParams.toString()}`;
    }

    private setGoogleMapType(type: string): void {
        const center = this.store.selectSnapshot(ActiveMapState.center);

        const gCenter = new google.maps.LatLng(center[1], center[0]);
        this.setMaxZoom(gCenter);
        this.gmap.setMapTypeId(type);
    }

    private _setCenter(center: Coordinate) {
        if (this.gmap) {
            const llCenter = toLonLat(center);
            const gCenter = new google.maps.LatLng(llCenter[1], llCenter[0]);
            this.gmap.setCenter(gCenter);
            this.setMaxZoom(gCenter);
        }
    }

    private _setZoom(zoom: number) {
        if (this.gmap) {
            this.gmap.setZoom(zoom);
        }
    }

    private _getMaxZoomFn(): (center: google.maps.LatLng) => void {
        return throttle(
            (center: google.maps.LatLng) => {
                const lastCenter = this.store.selectSnapshot(ActiveMapState.maxZoomCenter);
                if (2000 < MeasureService.distanceLatLon([center.lat(), center.lng()], lastCenter)) {
                    this.maxZoomService.getMaxZoomAtLatLng(center, (result: MaxZoomResult): void => {
                        const zoom: number = google.maps.MaxZoomStatus.OK === result.status ? result.zoom : 20;
                        const payload: MaxZoomPayload = {
                            googleMaxZoom: zoom,
                            maxZoomCenter: [center.lat(), center.lng()],
                        };
                        this.store.dispatch(new SetGoogleMaxImageryZoom(payload));
                    });
                }
            },
            1000,
            { trailing: true }
        );
    }

    private setThemeVariant(variant: 'light' | 'dark') {
        if (this.gmap) {
            this.store.selectOnce(AppConfigState.baseLayer).subscribe((config) => {
                this.gmap.setOptions({
                    styles: config.styles[variant],
                });
            });
        }
    }

    get apiAvailable(): boolean {
        return window.google?.maps as unknown as boolean;
    }

    get apiKey() {
        if (!this._apiKey) {
            this._apiKey = (this.mapConfig.getBaseLayer() as GoogleBaseLayerConfig).apiKey;
        }
        return this._apiKey;
    }

    get placesService() {
        if (!this._placesService) {
            this._placesService = new google.maps.places.PlacesService(this.gmap);
        }
        return this._placesService;
    }

    get streetViewPanoVisible$(): Observable<boolean> {
        return this.streetViewVisibleSubject.asObservable();
    }

    get streetViewPositionChanged$(): Observable<void> {
        return this.streetViewPositionChangedSubject.asObservable();
    }

    get noStreetViewSrc() {
        return 'assets/images/no-streetview.png';
    }
}
